import { Button } from "@lumi/ui-headless";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProviderCard } from "./components/ProviderCard";
import { useProviders } from "./hooks/useProviders";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";

/**
 * Providers Page - Admin Content Provider Management
 * Lists all content providers with statistics and search functionality
 */
// eslint-disable-next-line max-lines-per-function
export function ProvidersPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 12;

  // Fetch providers with current filters
  const { providers, pagination, loading, error } = useProviders(searchQuery, currentPage, limit);

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <div className="mx-auto space-y-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Content Providers</h1>
            <p className="text-sm text-neutral-400 pb-6">
              Manage content provider accounts and view submission statistics
            </p>
          </div>
          <Button variant="ghost-sm" onClick={() => navigate("/admin/review-queue")}>
            ← Back to Review Queue
          </Button>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by email or organization..."
              className="w-full px-4 py-2 bg-transparent border border-neutral-800 rounded text-white placeholder-neutral-500 focus:outline-none focus:border-lumi-accent"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
              >
                ✕
              </button>
            )}
          </div>
          <Button type="submit" variant="outline">
            Search
          </Button>
        </form>
      </div>

      {/* Stats Summary */}
      {!loading && !error && (
        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-neutral-500">
            {pagination.total} provider{pagination.total === 1 ? "" : "s"} found
            {searchQuery && ` for "${searchQuery}"`}
          </p>
          {searchQuery && (
            <Button onClick={handleClearSearch} variant="ghost-sm">
              Clear search
            </Button>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
          <p className="font-semibold">Error loading providers</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && providers.length === 0 && (
        <div className="text-center py-12">
          <div className="text-neutral-400 mb-2">No providers found</div>
          {searchQuery && (
            <Button onClick={handleClearSearch} variant="ghost-sm">
              Clear search to see all providers
            </Button>
          )}
        </div>
      )}

      {/* Providers Grid */}
      {!loading && !error && providers.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {providers.map((provider) => (
              <ProviderCard key={provider.id} provider={provider} />
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                variant="ghost"
              >
                Previous
              </Button>

              <span className="px-4 py-2 text-neutral-400">
                Page {pagination.page} of {pagination.totalPages}
              </span>

              <Button
                onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={currentPage === pagination.totalPages}
                variant="ghost"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
